import { template } from "@ember/template-compiler";
import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import { service } from "@ember/service";
import { buildWaiter } from "@ember/test-waiters";
import { modifier } from "ember-modifier";
import ConditionalLoadingSpinner from "discourse/components/conditional-loading-spinner";
import loadAce from "discourse/lib/load-ace-editor";
import { isTesting } from "discourse-common/config/environment";
import { bind } from "discourse-common/utils/decorators";
import I18n from "discourse-i18n";
const WAITER = buildWaiter("ace-editor");
const COLOR_VARS_REGEX = /\$(primary|secondary|tertiary|quaternary|header_background|header_primary|highlight|danger|success|love)(\s|;|-(low|medium|high))/g;
function overridePlaceholder(ace1) {
    const originalPlaceholderSetter1 = ace1.config.$defaultOptions.editor.placeholder.set;
    ace1.config.$defaultOptions.editor.placeholder.set = function() {
        if (!this.$updatePlaceholder) {
            const originalRendererOn1 = this.renderer.on;
            this.renderer.on = function() {};
            originalPlaceholderSetter1.call(this, ...arguments);
            this.renderer.on = originalRendererOn1;
            const originalUpdatePlaceholder1 = this.$updatePlaceholder;
            this.$updatePlaceholder = function() {
                originalUpdatePlaceholder1.call(this, ...arguments);
                if (this.renderer.placeholderNode) {
                    this.renderer.placeholderNode.innerHTML = this.$placeholder || "";
                }
            }.bind(this);
            this.on("input", this.$updatePlaceholder);
        }
        this.$updatePlaceholder();
    };
}
// Args:
// @content
// @mode
// @disabled (boolean)
// @onChange
// @editorId
// @theme
// @autofocus
// @placeholder
// @htmlPlaceholder (boolean)
// @save
// @submit
// @setWarning
export default class AceEditor extends Component {
    @service
    appEvents;
    @tracked
    isLoading = true;
    editor = null;
    ace = null;
    skipChangePropagation = false;
    setContent = modifier(()=>{
        if (this.args.content === this.editor.getSession().getValue()) {
            return;
        }
        this.skipChangePropagation = true;
        this.editor.getSession().setValue(this.args.content || "");
        this.skipChangePropagation = false;
        const token1 = WAITER.beginAsync();
        this.editor.renderer.once("afterRender", ()=>WAITER.endAsync(token1));
        return ()=>WAITER.endAsync(token1);
    });
    constructor(){
        super(...arguments);
        loadAce().then((ace1)=>{
            if (this.isDestroying || this.isDestroyed) {
                return;
            }
            this.ace = ace1;
            this.isLoading = false;
        });
        this.appEvents.on("ace:resize", this.resize);
        window.addEventListener("resize", this.resize);
        this._darkModeListener = window.matchMedia("(prefers-color-scheme: dark)");
        this._darkModeListener.addEventListener("change", this.setAceTheme);
    }
    willDestroy() {
        super.willDestroy(...arguments);
        this.editor?.destroy();
        this._darkModeListener?.removeEventListener("change", this.setAceTheme);
        window.removeEventListener("resize", this.resize);
        this.appEvents.off("ace:resize", this.resize);
    }
    @bind
    setupAce(element1) {
        if (this.args.htmlPlaceholder) {
            overridePlaceholder(this.ace);
        }
        this.ace.config.set("useWorker", false);
        this.editor = this.ace.edit(element1);
        this.editor.setShowPrintMargin(false);
        this.editor.setOptions({
            fontSize: "14px",
            placeholder: this.args.placeholder
        });
        const session1 = this.editor.getSession();
        session1.setMode(`ace/mode/${this.mode}`);
        this.editor.on("change", ()=>{
            if (!this.skipChangePropagation) {
                this.args.onChange?.(session1.getValue());
            }
        });
        if (this.args.save) {
            this.editor.commands.addCommand({
                name: "save",
                exec: ()=>this.args.save(),
                bindKey: {
                    mac: "cmd-s",
                    win: "ctrl-s"
                }
            });
        }
        if (this.args.submit) {
            this.editor.commands.addCommand({
                name: "submit",
                exec: ()=>this.args.submit(),
                bindKey: {
                    mac: "cmd-enter",
                    win: "ctrl-enter"
                }
            });
        }
        this.editor.on("blur", ()=>this.warnSCSSDeprecations());
        this.editor.$blockScrolling = Infinity;
        this.editor.renderer.setScrollMargin(10, 10);
        if (isTesting()) {
            element1.aceEditor = this.editor;
        }
        this.changeDisabledState();
        this.warnSCSSDeprecations();
        if (this.autofocus) {
            this.focus();
        }
        this.setAceTheme();
    }
    get mode() {
        return this.args.mode || "css";
    }
    @bind
    editorIdChanged() {
        if (this.autofocus) {
            this.focus();
        }
    }
    @bind
    modeChanged() {
        this.editor?.getSession().setMode(`ace/mode/${this.mode}`);
    }
    @bind
    placeholderChanged() {
        this.editor?.setOptions({
            placeholder: this.args.placeholder
        });
    }
    @bind
    changeDisabledState() {
        this.editor?.setOptions({
            readOnly: this.args.disabled,
            highlightActiveLine: !this.args.disabled,
            highlightGutterLine: !this.args.disabled
        });
        this.editor?.container.parentNode.parentNode.setAttribute("data-disabled", !!this.args.disabled);
    }
    warnSCSSDeprecations() {
        if (this.mode !== "scss" || this.args.editorId.startsWith("color_definitions") || !this.editor) {
            return;
        }
        let warnings1 = this.args.content.split("\n").map((line1, row1)=>{
            if (line1.match(COLOR_VARS_REGEX)) {
                return {
                    row: row1,
                    column: 0,
                    text: I18n.t("admin.customize.theme.scss_warning_inline"),
                    type: "warning"
                };
            }
        }).filter(Boolean);
        this.editor.getSession().setAnnotations(warnings1);
        this.args.setWarning?.(warnings1.length ? I18n.t("admin.customize.theme.scss_color_variables_warning") : false);
    }
    @bind
    setAceTheme() {
        const schemeType1 = getComputedStyle(document.body).getPropertyValue("--scheme-type").trim();
        const aceTheme1 = schemeType1 === "dark" ? "chaos" : "chrome";
        this.editor.setTheme(`ace/theme/${aceTheme1}`);
    }
    @bind
    resize() {
        this.editor?.resize();
    }
    @bind
    focus() {
        if (this.editor) {
            this.editor.focus();
            this.editor.navigateFileEnd();
        }
    }
    static{
        template(`
    <div class="ace-wrapper">
      <ConditionalLoadingSpinner @condition={{this.isLoading}} @size="small">
        <div
          {{didInsert this.setupAce}}
          {{this.setContent}}
          {{didUpdate this.editorIdChanged @editorId}}
          {{didUpdate this.modeChanged @mode}}
          {{didUpdate this.placeholderChanged @placeholder}}
          {{didUpdate this.changeDisabledState @disabled}}
          class="ace"
          ...attributes
        >
        </div>
      </ConditionalLoadingSpinner>
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
